import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';
import { isNotNullOrUndefined } from 'Util/TypeGuards';
import * as Enums from '../Enums';
import { TimeUnit } from 'Util/TimeUnit';

export interface IBookingCutoffEntityAttributes extends IModelAttributes {
	timeBeforeDepartureValue: number;
	timeBeforeDepartureUnit: TimeUnit;
	feeAmount: number;
	bookingType: Enums.bookingType;
	cutoffType: Enums.cutoffType;
	moveToEarlierTime: boolean;
	moveToLaterTime: boolean;
	applyToDepartureLegOnly?: boolean;
	applyToReturnLegOnly?: boolean;
	refundType: Enums.refundType;

	routes: Array<Models.BookingCutoffRoute | Models.IBookingCutoffRouteAttributes>;
	staticFee?: boolean;
	appliesToStandardUsers?: boolean;
	appliesToAllCustomerTypes?: boolean;
	isNoShowRule?: boolean;
	appliesAnytime?: boolean;
	userDisplayNames: Array<Models.BookingCutoffsUserDisplayNames | Models.IBookingCutoffsUserDisplayNamesAttributes>;
}
@entity('BookingCutoffEntity', 'Booking Cutoff')
export default class BookingCutoffEntity extends Model implements IBookingCutoffEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	/**
	 * The number of time units before a ferry trip departs (do not apply pricing before this point)
	 */
	@observable
	@attribute<BookingCutoffEntity, number>()
	public timeBeforeDepartureValue: number;

	@observable
	@attribute<BookingCutoffEntity, TimeUnit>()
	public timeBeforeDepartureUnit: TimeUnit = TimeUnit.HOURS;

	/**
	 * The percentage value to calculate the fee based on the booking cost
	 */
	@observable
	@attribute<BookingCutoffEntity, number>()
	public feeAmount: number;

	/**
	 * The type of booking that the cut off entity applies to
	 */
	@observable
	@attribute<BookingCutoffEntity, Enums.bookingType>()
	public bookingType: Enums.bookingType;

	/**
	 * The type of action the fee is applicable to
	 */
	@observable
	@attribute<BookingCutoffEntity, Enums.cutoffType>()
	public cutoffType: Enums.cutoffType;

	@observable
	@attribute<BookingCutoffEntity, boolean>()
	public moveToEarlierTime: boolean;

	@observable
	@attribute<BookingCutoffEntity, boolean>()
	public moveToLaterTime: boolean;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public routes: Models.BookingCutoffRoute[] = [];

	@observable
	@attribute<BookingCutoffEntity, boolean>()
	public staticFee?: boolean;
	public appliesToStandardUsers?: boolean;
	public appliesToAllCustomerTypes?: boolean;
	public isNoShowRule?: boolean;
	public appliesAnytime?: boolean;

	@observable
	@attribute<BookingCutoffEntity, boolean>()
	public applyToDepartureLegOnly?: boolean;

	@observable
	@attribute<BookingCutoffEntity, boolean>()
	public applyToReturnLegOnly?: boolean;

	@observable
	@attribute<BookingCutoffEntity, Enums.refundType>()
	public refundType: Enums.refundType = 'ANY_REFUND';

	@observable
	@attribute({ isReference: true, manyReference: true })
	public userDisplayNames: Models.BookingCutoffsUserDisplayNames[] = [];

	constructor(attributes?: Partial<IBookingCutoffEntityAttributes>) {
		super(attributes);

		this.routes = [];
		this.userDisplayNames = [];

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<IBookingCutoffEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.timeBeforeDepartureValue !== undefined) {
				this.timeBeforeDepartureValue = attributes.timeBeforeDepartureValue;
			}
			if (attributes.timeBeforeDepartureUnit !== undefined) {
				this.timeBeforeDepartureUnit = attributes.timeBeforeDepartureUnit;
			}
			if (attributes.feeAmount !== undefined) {
				this.feeAmount = attributes.feeAmount;
			}
			if (attributes.bookingType !== undefined) {
				this.bookingType = attributes.bookingType;
			}
			if (attributes.cutoffType !== undefined) {
				this.cutoffType = attributes.cutoffType;
			}
			if (attributes.moveToEarlierTime !== undefined) {
				this.moveToEarlierTime = attributes.moveToEarlierTime;
			}
			if (attributes.moveToLaterTime !== undefined) {
				this.moveToLaterTime = attributes.moveToLaterTime;
			}
			if (isNotNullOrUndefined(attributes.appliesToStandardUsers)) {
				this.appliesToStandardUsers = attributes.appliesToStandardUsers;
			}
			if (isNotNullOrUndefined(attributes.appliesToAllCustomerTypes)) {
				this.appliesToAllCustomerTypes = attributes.appliesToAllCustomerTypes;
			}
			if (isNotNullOrUndefined(attributes.appliesAnytime)) {
				this.appliesAnytime = attributes.appliesAnytime;
			}
			if (isNotNullOrUndefined(attributes.isNoShowRule)) {
				this.isNoShowRule = attributes.isNoShowRule;
			}
			if (isNotNullOrUndefined(attributes.staticFee)) {
				this.staticFee = attributes.staticFee;
			}
			if (isNotNullOrUndefined(attributes.applyToDepartureLegOnly)) {
				this.applyToDepartureLegOnly = attributes.applyToDepartureLegOnly;
			}
			if (isNotNullOrUndefined(attributes.applyToReturnLegOnly)) {
				this.applyToReturnLegOnly = attributes.applyToReturnLegOnly;
			}
			if (attributes.refundType !== undefined) {
				this.refundType = attributes.refundType;
			}
			if (attributes.routes !== undefined && Array.isArray(attributes.routes)) {
				if (!this.routes) {
					this.routes = [];
				}
				for (const model of attributes.routes) {
					if (model instanceof Models.BookingCutoffRoute) {
						this.routes.push(model);
					} else {
						this.routes.push(new Models.BookingCutoffRoute(model));
					}
				}
			}
			if (attributes.userDisplayNames !== undefined && Array.isArray(attributes.userDisplayNames)) {
				if (!this.userDisplayNames) {
					this.userDisplayNames = [];
				}
				for (const model of attributes.userDisplayNames) {
					if (model instanceof Models.BookingCutoffsUserDisplayNames) {
						this.userDisplayNames.push(model);
					} else {
						this.userDisplayNames.push(new Models.BookingCutoffsUserDisplayNames(model));
					}
				}
			}
		}
	}
}
